import { useMemo, useState } from "react";
import { Play, Search, X } from "lucide-react";
import { PLAN_DEFAULTS } from "@sprout/shared/plans";
import { HelpCard, Overline, Questions, useDemo, type QA } from "../Layout";
import { useActiveSection, usePage } from "../router";
import { SUPPORT_EMAIL } from "../links";
import { FREE, PAID, limitValue, planPrice } from "../plan-format";

type Group = { id: string; title: string; items: (QA & { a: string })[] };

const paid = planPrice(PAID);

/** Answers are plain text so the search can match them. */
export const FAQ: Group[] = [
  {
    id: "getting-started",
    title: "Getting started",
    items: [
      {
        q: "How do I set up Sprout?",
        a: "Create a family with your name, email and a parent PIN, then add your first child. Pick a few of the 15 everyday challenges, and pair a tablet by opening the wall app and scanning its code with your phone. It takes about five minutes, and no card is needed.",
      },
      {
        q: "Do my kids need their own accounts?",
        a: "No. Each child gets a profile inside your family, with no email or password. On the wall screen they simply tap their profile to start.",
      },
      {
        q: "Can two parents share one family?",
        a: `Yes. Invite other grown-ups from the parent app. Each signs in with their own email and password, and a family can have up to ${FREE.limits.guardians} guardians, including pending invitations.`,
      },
    ],
  },
  {
    id: "wall-app",
    title: "The wall app",
    items: [
      {
        q: "What can I use as the wall screen?",
        a: "Any tablet or screen with a modern browser. Open the wall app on it, then scan the QR code with your phone or type its 8-character code in Account → Wall devices. Codes renew every 10 minutes.",
      },
      {
        q: "Can I lock the screen when it’s not chore time?",
        a: "Yes. Every paired screen has a name, and you can lock, unlock or disconnect it live from your phone. The change reaches the screen right away.",
      },
      {
        q: "Does my child need a camera?",
        a: "No. Choose photo, video or no proof for each task. No-proof tasks finish with a tap and never open the camera.",
      },
    ],
  },
  {
    id: "privacy",
    title: "Photos, video & privacy",
    items: [
      {
        q: "Who can see the photos and videos?",
        a: `Only the grown-ups in your family, with parent controls unlocked. Proof sits in private storage, is never sent to AI, and is deleted after ${PLAN_DEFAULTS.retentionDays} days unless you change that.`,
      },
      {
        q: "Does Sprout use facial recognition?",
        a: "No. Children choose their profile by tapping it, and nothing checks faces or decides on its own whether a chore was done. A grown-up always reviews.",
      },
      {
        q: "Does Sprout use AI?",
        a: `Only if you choose to. AI coaching is part of ${PAID.name} and stays off until a grown-up turns on the family’s AI switch. It sees only the task and your child’s age, never photos or videos. Without it, Buddy uses guided coaching.`,
      },
      {
        q: "Can I download or delete our data?",
        a: "Yes. From Account → Privacy you can download your family’s records as a file, clear proof early, or delete the account.",
      },
    ],
  },
  {
    id: "points",
    title: "Points & rewards",
    items: [
      {
        q: "How do points work?",
        a: "Each challenge is worth a few points. When you approve a try, its points land once. Children put them toward rewards you create together, like a movie night, a family dinner or an adventure afternoon.",
      },
      {
        q: "Is there screen time after a chore?",
        a: "Just a little. Each try unlocks one round of play: a 15–60 second picture game or one of your family’s own clips. It never turns into endless scrolling.",
      },
    ],
  },
  {
    id: "plans",
    title: "Plans",
    items: [
      {
        q: "Is Sprout really free?",
        a: `Yes. Routines, review, wall screens, photo and video proof, skills and progress, reward games and co-guardians are free for every family, with no card needed. ${FREE.name} includes ${FREE.limits.wallDevices} wall screens and ${limitValue("proofStorageMb", FREE.limits.proofStorageMb)} of proof storage.`,
      },
      {
        q: "What does Plus add?",
        a: `${PAID.name} adds AI coaching, proof kept for up to ${PAID.limits.proofRetentionMaxDays} days, ${PAID.limits.wallDevices} wall screens, ${limitValue("proofStorageMb", PAID.limits.proofStorageMb)} of proof storage, videos up to ${PAID.limits.maxVideoSeconds} seconds and priority support. It’s ${paid.amount}${paid.per === "/mo" ? " a month" : " a year"}${paid.note ? ", " + paid.note : ""}.`,
      },
    ],
  },
];

const normalise = (s: string) => s.toLowerCase().replace(/[’']/g, "'");

export default function Faq() {
  usePage("FAQ · Sprout");
  const openDemo = useDemo();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(FAQ[0]!.id);
  const groups = useMemo(() => {
    const words = normalise(query).split(/\s+/).filter(Boolean);
    if (!words.length) return FAQ;
    return FAQ.map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        const text = normalise(item.q + " " + item.a + " " + g.title);
        return words.every((w) => text.includes(w));
      }),
    })).filter((g) => g.items.length);
  }, [query]);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  useActiveSection(
    groups.map((g) => g.id),
    setActive,
  );
  return (
    <>
      <section className="page-head split faq-head site-wrap">
        <div>
          <Overline>FAQ</Overline>
          <h1 className="site-h1">Questions parents ask</h1>
          <p>Short, honest answers about the wall screen, privacy, points and plans.</p>
        </div>
        <label className="faq-search">
          <Search size={19} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions"
            aria-label="Search questions"
          />
          {query && (
            <button type="button" aria-label="Clear search" onClick={() => setQuery("")}>
              <X size={17} />
            </button>
          )}
        </label>
      </section>

      <section className="page-body site-wrap">
        <nav className="page-toc" aria-label="Topics">
          {groups.map((g) => (
            <a key={g.id} href={`#${g.id}`} aria-current={active === g.id ? "true" : undefined}>
              {g.title}
              <span>{g.items.length}</span>
            </a>
          ))}
        </nav>
        <div className="page-article faq-groups">
          <p className="sr-only" role="status">
            {query ? `${total} matching ${total === 1 ? "question" : "questions"}` : ""}
          </p>
          {groups.map((g) => (
            <section key={g.id} id={g.id}>
              <h2>{g.title}</h2>
              {/* While searching, every match opens so the answer is visible. */}
              <Questions key={query ? "q:" + query : "all"} items={g.items} openFirst openAll={!!query} />
            </section>
          ))}
          {!groups.length && (
            <p className="faq-empty">
              No questions match “{query}”.{" "}
              <button type="button" className="site-plain-link" onClick={() => setQuery("")}>
                Show all questions
              </button>
            </p>
          )}
          <HelpCard
            title="Still curious?"
            text={
              SUPPORT_EMAIL
                ? "Try the sample chore, or write to us. A person reads every note."
                : "Try the sample chore and see what your child would see."
            }
          >
            <button type="button" className="site-btn primary" onClick={() => openDemo()}>
              <Play size={15} fill="currentColor" /> Try a chore
            </button>
          </HelpCard>
        </div>
      </section>
    </>
  );
}
